import { isIOS, isStandalone } from './pushNotifications';
import { normalizeVenueId, resolveVenueIdFromLocation } from './venueParam';

/**
 * "Add to Home Screen" for the Save me button.
 *
 * What a page can do differs by device, so every tap ends in one of these modes:
 *   installed - already opened from the home screen icon: nothing to offer
 *   prompt    - the browser offered its own install dialog (Chrome / Edge on Android and desktop): show it
 *   in-app    - a web view inside another app (Facebook, WeChat...): it cannot add anything, the guest must open a browser
 *   ios       - iPhone / iPad: Apple gives pages no way to do it, so show how to use the Share menu
 *   manual    - any other browser without an install dialog: point at its own menu
 */

// Apps that open links in their own web view. None of them can add a site to the home screen.
const IN_APP_BROWSERS = [
  [/FBAN|FBAV|FB_IAB/i, 'Facebook'],
  [/Instagram/i, 'Instagram'],
  [/MicroMessenger/i, 'WeChat'],
  [/\bLine\//i, 'LINE'],
  [/Snapchat/i, 'Snapchat'],
  [/TikTok|musical_ly|BytedanceWebview/i, 'TikTok'],
  [/Twitter/i, 'X'],
  [/LinkedInApp/i, 'LinkedIn'],
  [/Pinterest/i, 'Pinterest'],
  [/\bGSA\//i, 'Google app'],
];

/** The in-app browser the guest is in (its name), or null. Android web views mark themselves with "wv". */
export function inAppBrowserName(nav = window.navigator) {
  const ua = nav.userAgent || '';
  const known = IN_APP_BROWSERS.find(([pattern]) => pattern.test(ua));
  if (known) return known[1];
  return /; wv\)/i.test(ua) ? 'this app' : null;
}

/** Safari is the only iOS browser where the Share button is in the toolbar; the others keep it in the address bar or menu. */
export function iosBrowser(nav = window.navigator) {
  const ua = nav.userAgent || '';
  if (/CriOS/i.test(ua)) return 'Chrome';
  if (/EdgiOS/i.test(ua)) return 'Edge';
  if (/FxiOS/i.test(ua)) return 'Firefox';
  if (/OPiOS/i.test(ua)) return 'Opera';
  return 'Safari';
}

/** A phone or tablet (an iPad that reports itself as a Mac included). Computers need different steps. */
export const isMobileDevice = (nav = window.navigator) => isIOS(nav) || /Android|Mobile/i.test(nav.userAgent || '');

// ------------------------------------------------------------ the browser's install offer, kept for the whole page
// The browser offers the install dialog once per page load, early, and a component that mounts later (or again,
// after the guest opened a detail page and came back) would miss it. So it is kept here, outside React.

let offer = null;
let offerVenue = ''; // empty means unresolved; an offer captured for A must never be consumed from B
let installedVenues = Object.freeze([]); // retain every confirmed venue in this page session
// appinstalled carries no venue id. Keep one attempt until it finishes or is declined,
// so an accepted A can never be replaced by B while its completion event is pending.
let pendingInstall = null;
let stopCapture = null;

// A slow browser gets visible recovery guidance, without discarding the attempt's identity.
const DIALOG_GRACE_MS = 60 * 1000;
const pendingResult = () => ({
  guide: pendingInstall.phase === 'installing' || Date.now() - pendingInstall.startedAt >= DIALOG_GRACE_MS
    ? 'install-pending' : null,
  outcome: 'pending',
});
const listeners = new Set();

const notify = () => listeners.forEach((listener) => listener());

/** Start listening for the browser's install offer. Call once when the app starts; calling again does nothing. */
export function startInstallCapture(win = window) {
  if (stopCapture) return stopCapture;
  const onOffer = (event) => {
    event.preventDefault(); // keep the browser's own mini banner away: the Save me button is the entry point
    offer = event;
    offerVenue = resolveVenueIdFromLocation(win.location);
    notify();
  };
  const onInstalled = () => {
    const venueId = normalizeVenueId(pendingInstall ? pendingInstall.venueId : resolveVenueIdFromLocation(win.location));
    // An event with no identifiable venue cannot mark every venue as installed.
    if (venueId && !installedVenues.includes(venueId)) installedVenues = Object.freeze([...installedVenues, venueId]);
    pendingInstall = null;
    // A can finish after the browser has already offered B. Keep B's independent offer.
    if (!offerVenue || offerVenue === venueId) {
      offer = null;
      offerVenue = '';
    }
    notify();
  };
  win.addEventListener('beforeinstallprompt', onOffer);
  win.addEventListener('appinstalled', onInstalled);
  stopCapture = () => {
    win.removeEventListener('beforeinstallprompt', onOffer);
    win.removeEventListener('appinstalled', onInstalled);
    offer = null;
    offerVenue = '';
    installedVenues = Object.freeze([]);
    pendingInstall = null;
    stopCapture = null;
  };
  return stopCapture;
}

/** What the page knows right now. The same object is returned until something changes. */
let snapshot = { hasOffer: false, offerVenue: '', installedVenues };
export function getInstallState() {
  if (snapshot.hasOffer !== Boolean(offer) || snapshot.offerVenue !== offerVenue || snapshot.installedVenues !== installedVenues) {
    snapshot = { hasOffer: Boolean(offer), offerVenue, installedVenues };
  }
  return snapshot;
}

export function subscribeInstallState(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ------------------------------------------------------------ what a tap does

/**
 * Which of the modes above applies right now, for the venue on screen. Installing one venue puts that venue on the
 * Home Screen, not the others, so another venue opened in the same visit is still offered.
 */
export function installMode(win = window, state = getInstallState(), venueId = resolveVenueIdFromLocation(win.location || {})) {
  const id = normalizeVenueId(venueId);
  const installedHere = Boolean(id && state.installedVenues?.includes(id));
  if (installedHere || isStandalone(win)) return 'installed';
  if (inAppBrowserName(win.navigator)) return 'in-app';
  if (id && state.hasOffer && (!state.offerVenue || normalizeVenueId(state.offerVenue) === id)) return 'prompt';
  if (isIOS(win.navigator)) return 'ios';
  return 'manual';
}

/**
 * Shows the browser's install dialog. The offer can be used once; after that the browser has to make a new one.
 * Resolves 'accepted', 'dismissed', 'pending', or 'unavailable'. Acceptance is not installation completion.
 */
export async function requestInstall(venueId = resolveVenueIdFromLocation()) {
  if (pendingInstall) return 'pending';
  const current = offer;
  const id = normalizeVenueId(venueId);
  // Legacy links can receive an offer before VenueGate resolves the URL. Bind an unresolved
  // offer to the real venue at this tap, before any asynchronous browser response can arrive.
  if (!current || !id || (offerVenue && offerVenue !== id) || resolveVenueIdFromLocation() !== id) return 'unavailable';
  offer = null;
  offerVenue = '';
  const attempt = { venueId: id, startedAt: Date.now(), phase: 'prompting' };
  pendingInstall = attempt;
  notify();
  try {
    // If prompt() fails, the browser may reject userChoice too and nobody would be awaiting it: mark it handled
    const choiceMade = current.userChoice;
    if (choiceMade && typeof choiceMade.catch === 'function') choiceMade.catch(() => {});
    await current.prompt();
    const choice = await choiceMade;
    if (choice && choice.outcome === 'accepted') {
      if (pendingInstall === attempt) attempt.phase = 'installing';
      return 'accepted';
    }
    if (pendingInstall === attempt) pendingInstall = null;
    return 'dismissed';
  } catch (error) {
    if (pendingInstall === attempt) pendingInstall = null;
    return 'unavailable';
  }
}

/**
 * Handles one tap on Save me. Resolves { guide, outcome }: the help to show ('ios' | 'in-app' | 'manual'), or
 * 'install-pending' while an earlier installation finishes, or guide null when nothing needs showing,
 * with outcome saying why: 'accepted' / 'dismissed' (the browser's own dialog
 * took over), 'pending' (that dialog is still open), 'installed' (already on the Home Screen).
 */
export async function startInstall(win = window, venueId = resolveVenueIdFromLocation(win.location || {})) {
  // Preserve the outstanding venue even after acceptance, including slow installation completion.
  if (pendingInstall) return pendingResult();
  let mode = installMode(win, getInstallState(), venueId);
  if (mode === 'prompt') {
    const outcome = await requestInstall(venueId);
    if (outcome !== 'unavailable') return { guide: null, outcome };
    mode = installMode(win, getInstallState(), venueId); // the dialog could not open: fall back to telling the guest how
  }
  if (mode === 'installed') return { guide: null, outcome: 'installed' };
  return { guide: mode === 'prompt' ? 'manual' : mode, outcome: null };
}
